import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';

import { CompanyMapper, InvoiceMapper } from '@shared-classes';
import {
  DianCompany,
  Invoice,
  XMLResponse,
  InvoiceRequest,
  AlertConfig,
  AlertType,
  ClientData,
} from '@shared-models';
import { DianService } from '@shared-services';
import { AlertService } from '@shared-services';

import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { CalculateDetailsService } from 'src/app/@pages/details/calculate-details.service';
import { ElectronicInvoicePdfService } from 'src/app/@pages/details/electronic-invoice-pdf.service';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { AppState } from 'src/app/state/app.state';
import { selectCompaniesList } from 'src/app/state/selectors/user-companies.selectors';
import { environment } from 'src/environments/environment';

import { DialogComponent } from '../../../../@shared/components/dialog/dialog.component';
import { CookieAuthService } from '../../../../@shared/storage-variables/cookie-auth.service';

@Injectable({
  providedIn: 'root',
})
export class DianInvoiceService {
  private spinnerService = inject(NgxSpinnerService);
  private translate = inject(TranslateService);
  private quotationService = inject(QuotationsService);
  dialog = inject(MatDialog);
  private dianService = inject(DianService);
  private store = inject(Store<AppState>);
  private cookieAuthService = inject(CookieAuthService);
  private alertService = inject(AlertService);
  private spinner = inject(NgxSpinnerService);
  private destroyRef = inject(DestroyRef);
  private calculateDetailsService = inject(CalculateDetailsService);
  private electronicInvoicePdfService = inject(ElectronicInvoicePdfService);

  companyId = '';
  companyName = '';
  companyObject = {} as IStorageCompany;

  getAuthValues() {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.companyId = this.companyObject.Id_company!.toString();
  }

  async sendDian(invoiceId: number) {
    try {
      this.getAuthValues();
      const invoice = await this.getInvoice(invoiceId);
      const isValid = await this.validateInvoiceSended(invoice);
      if (!isValid) {
        throw new Error('The invoice could not be sent.');
      }
      const dianCompany = await this.createDianCompany(
        invoice.billyInvoice.voucher!,
        this.companyId.toString(),
      );
      const confirmed = await this.askUserConfirmation();
      if (!confirmed) {
        throw new Error('User cancelled the operation.');
      }
      this.spinner.show();
      const dianResponse = await this.sendRequest(invoice, dianCompany);
      return dianResponse;
    } catch (error) {
      // Hide the spinner on any rejection path (DIAN rejection, company/prefix
      // build failure, network error) so the error dialog stays dismissible.
      // Matches dian-note / dian-adjustment-note / dian-supporting-document.
      this.spinner.hide();
      return Promise.reject(error);
    }
  }

  askUserConfirmation(): Promise<boolean> {
    return new Promise((resolve) => {
      this.alertService
        .open({
          title: this.translate.instant('Warning'),
          message: this.translate.instant(
            'You are about to send this invoice to DIAN, are you sure?.',
          ),
          type: 'warning',
          acceptText: this.translate.instant('Yes'),
          rejectText: this.translate.instant('Abort'),
        })
        .subscribe({
          accept: () => resolve(true),
          reject: () => resolve(false),
          close: () => resolve(false),
        });
    });
  }

  async getInvoice(id: number) {
    return new Promise<Invoice>((resolve) => {
      this.quotationService
        .getQuotation(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((item: Invoice) => {
          resolve(item);
        });
    });
  }

  async validateInvoiceSended(invoice: Invoice): Promise<boolean> {
    const cufe = invoice.billyInvoice?.cufe;
    if (!cufe) {
      return true;
    }
    this.spinnerService.show();
    try {
      const response = await firstValueFrom(
        this.dianService.getStatus(cufe, this.companyId),
      );
      this.spinnerService.hide();
      await new Promise<void>((resolve) => {
        this.alertService
          .open({
            title: this.translate.instant('This invoice has already been sent'),
            message: `${this.translate.instant('Status')}: ${response.StatusDescription}`,
            type: 'warning',
            acceptText: 'Ok',
          })
          .subscribe({ accept: () => resolve() });
      });
      throw new Error('Invoice already sent');
    } catch (error) {
      this.spinnerService.hide();
      await new Promise<void>((resolve) => {
        this.alertService
          .open({
            title: this.translate.instant('Information'),
            message: `CUFE: ${cufe}`,
            type: 'info',
            acceptText: 'Ok',
          })
          .subscribe({ accept: () => resolve() });
      });
      throw error;
    }
  }

  async createDianCompany(voucher: number, companyId: string) {
    return new Promise<DianCompany>((resolve, reject) => {
      const companyList = this.store.select(selectCompaniesList);
      companyList.subscribe((data) => {
        const company = data.find(
          (item) => item.Id_company.toString() === companyId.toString(),
        );
        const prefixRaw = company!.prefixes.find(
          (p) => p.voucher === voucher && p.document_type === 'F',
        )!;
        const prefixHasNulls = Object.values(prefixRaw).some(
          (prefix) => prefix === null || prefix === undefined,
        );
        if (prefixHasNulls) {
          this.showErrorAlert(
            this.translate.instant('global.company.prefix.prefix-config-error'),
          );
          reject('The prefix is not configured correctly.');
        } else {
          const dianCompany = CompanyMapper.mapper(prefixRaw, company!);
          resolve(dianCompany);
        }
      });
    });
  }

  async sendRequest(invoice: Invoice, dianCompany: DianCompany) {
    return new Promise((resolve, reject) => {
      this.selectCurrency()
        .then((currency) => {
          const dianInvoice = InvoiceMapper.mapper(invoice, currency);
          const dianRequiredObject: InvoiceRequest = {
            company: dianCompany,
            invoice: dianInvoice,
          };
          this.dianService.sendBillSyncInvoice(dianRequiredObject).subscribe(
            async (dianResponse: XMLResponse) => {
              if (
                dianResponse.StatusCode === '00' ||
                (dianResponse.string &&
                  dianResponse.string.includes('Regla: 90'))
                // Rule: 90 is when the document has already been processed previously, then proceed to save the cufe.
              ) {
                await this.onSuccessFullRequest(
                  invoice,
                  currency,
                  dianResponse,
                );
                resolve(dianResponse);
              } else {
                this.alertService.open(this.buildAlert(dianResponse));
                reject(dianResponse.StatusMessage);
              }
            },
            async (error) => {
              await this.onUnsuccessFullRequest(error);
              reject(error);
            },
          );
        })
        .catch(async (error) => {
          await this.onUnsuccessFullRequest(error);
          reject(error);
        });
    });
  }

  async selectCurrency() {
    return new Promise<string>((resolve, reject) => {
      this.spinner.hide();
      const currencyError = this.translate.instant('No currency selected.');
      this.dialog
        .open(DialogComponent, {
          data: { currencySelection: true },
        })
        .afterClosed()
        .subscribe((dialogResult) => {
          if (dialogResult === undefined || dialogResult === null) {
            this.dialog.closeAll();
            reject(currencyError);
          } else if (dialogResult === '') {
            reject(currencyError);
          } else {
            this.spinner.show();
            resolve(dialogResult);
          }
        });
    });
  }

  buildAlert(dianResponse: XMLResponse): AlertConfig {
    let alertType: AlertType = 'success';
    let alertMessage = dianResponse.StatusMessage;
    const statusCode = dianResponse.StatusCode;
    if (statusCode === '99') {
      // Code 99 is the identifier for the Dian's response to mandatory warnings.
      alertType = 'error';
      alertMessage = this.translate.instant('global.dian.mandatory-warning');
    } else if (statusCode !== '00') {
      // Code 00 is the code for a satisfactory response, so anything other than that or the previous 99 is an error.
      alertType = 'error';
    }
    // This “string” parameter is created when converting the DIAN response to JSON in the api, it contains additional details if there are errors or warnings.
    if (dianResponse.string) {
      alertMessage = `${alertMessage}
      \n${dianResponse.string}`;
    }
    return {
      title: dianResponse.StatusDescription,
      message: alertMessage,
      type: alertType,
      showClose: true,
    };
  }

  async onSuccessFullRequest(
    invoice: Invoice,
    currency: string,
    dianResponse: XMLResponse,
  ) {
    return new Promise<unknown>((resolve, reject) => {
      (async () => {
        try {
          const clientData = this.prepareClientData(
            invoice,
            invoice.billyInvoice.invoice_number,
          );
          const pdfFile = await this.generateInvoicePdf(invoice, dianResponse);
          const xmlFile = dianResponse.AttachedDocument || '';
          this.quotationService
            .setCufeInvoice(
              parseInt(invoice.id_factura.toString()),
              dianResponse.XmlDocumentKey,
              dianResponse.StatusCode,
              currency,
              clientData,
              pdfFile,
              xmlFile,
            )
            .subscribe({
              next: (result) => {
                this.spinner.hide();
                this.alertService.open(this.buildAlert(dianResponse));
                resolve(result);
              },
              error: (error) => {
                this.spinner.hide();
                this.showErrorAlert(error);
                reject(error);
              },
            });
        } catch (error) {
          this.spinner.hide();
          this.alertService.open({
            title: 'Error',
            message: 'Error preparing invoice data for email',
            type: 'error',
            showClose: true,
          });
          reject(error);
        }
      })();
    });
  }

  prepareClientData(
    invoice: Invoice,
    documentNumber: string,
    documentType?: string,
  ): ClientData {
    const clientData = {
      name: invoice.cliente?.nombre || '',
      email: invoice.cliente?.email || '',
      invoiceNumber: documentNumber || '',
      documentType: documentType || '01', // Invoice type is always "01" for DIAN electronic invoicing
    };
    return clientData;
  }

  private async generateInvoicePdf(
    invoice: Invoice,
    dianResponse: XMLResponse,
  ): Promise<string> {
    try {
      // Configure the calculation service with the invoice data
      await this.calculateDetailsService.setDocument(
        parseInt(invoice.id_factura.toString()),
        this.companyId,
      );

      // Update QR URL with DIAN and CUFE information
      this.calculateDetailsService.qrUrl = this.generateQrUrl(dianResponse);

      const pdfObject = {
        invoiceObject: this.calculateDetailsService.invoiceObject,
        invoiceCount: this.calculateDetailsService.invoiceCount,
        sales: this.calculateDetailsService.sales,
        subtotal: this.calculateDetailsService.subtotal,
        taxCalculated: this.calculateDetailsService.taxCalculated,
        otherTaxCalculated: this.calculateDetailsService.otherTaxes,
        discount: this.calculateDetailsService.discount,
        total: this.calculateDetailsService.total,
        qrUrl: this.calculateDetailsService.qrUrl,
        rateBCV: this.calculateDetailsService.rateBCV,
        driverObject: this.calculateDetailsService.driverObject,
        company: this.companyId,
        open: false,
        cufe: dianResponse.XmlDocumentKey,
      };
      // Generate the PDF using the DIAN electronic invoicing service.
      const pdfDocument =
        await this.electronicInvoicePdfService.createInvoicePdf(pdfObject);

      // Convert PDF to base64
      return new Promise((resolve) => {
        if (pdfDocument && pdfDocument.getBase64) {
          pdfDocument.getBase64((base64: string) => {
            resolve(base64);
          });
        } else {
          resolve('');
        }
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      return '';
    }
  }

  generateQrUrl(dianResponse: XMLResponse): string {
    const cufe = dianResponse.XmlDocumentKey;
    return `${environment.dian_vpfe}/document/searchqr?documentkey=${cufe}`;
  }

  async onUnsuccessFullRequest(error: undefined | string) {
    return new Promise((reject) => {
      this.spinner.hide();
      this.showErrorAlert(error);
      reject(error);
    });
  }

  private showErrorAlert(error: undefined | string) {
    this.alertService.open({
      title: this.translate.instant('global.dian.invoices.error'),
      message: error!,
      type: 'error',
      showClose: true,
    });
  }
}
